import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import {
    Lock, User, Key, RefreshCw, BarChart3, List, Activity,
    LogOut, Users, FileText, AlertCircle,
    X, Maximize2, Server, Cpu, Bot, CheckCircle
} from 'lucide-react';

// Chart.js 컴포넌트 등록
ChartJS.register(
    ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
    PointElement, LineElement, BarElement, Title
);

// API 주소 설정
const API_BASE = process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === 'chatbot.kiast.or.kr'
        ? ''
        : `http://${window.location.hostname}:8000`);

// [Component] Score Chart (Rerank 점수 시각화)
const ScoreChart = ({ scores }) => {
    if (!scores || scores.length === 0) {
        return <div className="h-32 flex items-center justify-center text-xs text-slate-400">점수 데이터 없음</div>;
    }

    const data = {
        labels: scores.map((_, i) => `Doc ${i + 1}`),
        datasets: [{
            label: 'Score',
            data: scores,
            backgroundColor: scores.map(s => s >= -2.0 ? 'rgba(59, 130, 246, 0.7)' : 'rgba(248, 113, 113, 0.7)'),
            borderRadius: 4,
            borderWidth: 0
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: {
                beginAtZero: false,
                grid: { color: '#f1f5f9' },
                ticks: { font: { size: 10 } }
            },
            x: { display: false }
        }
    };

    return (
        <div className="h-40 w-full">
            <Bar data={data} options={options} />
        </div>
    );
};

// [헬퍼] 날짜 포맷팅 (컴포넌트 외부로 이동하여 의존성 제거)
const formatCompactTime = (isoString) => {
    if (!isoString) return '-';
    try {
        const date = new Date(isoString);
        const yy = String(date.getFullYear()).slice(2);
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const time = date.toLocaleTimeString('ko-KR', { hour12: false });
        const timeWithoutSpace = time.replace(/\s/g, '');

        return (
            <div className="flex flex-col items-center leading-tight">
                <span className="font-bold text-slate-600">{yy}.{mm}.{dd}.</span>
                <span className="text-[10px] text-slate-400 font-mono">{timeWithoutSpace}</span>
            </div>
        );
    } catch (e) {
        return isoString;
    }
};

// [헬퍼] API 헤더 생성 (컴포넌트 외부로 이동)
const getHeaders = (authHeader = null) => {
    const headers = { 'Content-Type': 'application/json' };
    const auth = authHeader || sessionStorage.getItem('admin_auth');
    if (auth) headers['Authorization'] = auth;
    const sid = sessionStorage.getItem('kiast_session_id');
    if (sid) headers['X-Session-ID'] = sid;
    return headers;
};

export default function AdminDashboard() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);

    // [상태 관리] 모달 팝업을 위한 선택된 로그
    const [selectedLog, setSelectedLog] = useState(null);

    // [핸들러] 로그아웃 (useCallback 적용)
    const handleLogout = useCallback(() => {
        sessionStorage.removeItem('admin_auth');
        setIsLoggedIn(false);
        setStats(null);
        setLogs([]);
    }, []);

    // [핸들러] 데이터 로드 (useCallback 적용, handleLogout 의존성 추가)
    const fetchData = useCallback(async (authHeader = null) => {
        setLoading(true);
        try {
            const headers = getHeaders(authHeader);
            const statRes = await fetch(`${API_BASE}/api/admin/stats`, { headers });
            if (statRes.ok) {
                setStats(await statRes.json());
            } else if (statRes.status === 401) {
                handleLogout();
                return;
            }
            const logRes = await fetch(`${API_BASE}/api/admin/logs?limit=100`, { headers });
            if (logRes.ok) {
                setLogs(await logRes.json());
            }
            const sugRes = await fetch(`${API_BASE}/api/admin/suggestions`, { headers });
            if (sugRes.ok) {
                setSuggestions(await sugRes.json());
            }
        } catch (e) { console.error("Fetch Error:", e); } finally { setLoading(false); }
    }, [handleLogout]);

    const handleApprove = async (suggestionId, alias, officialTerm) => {
        try {
            const headers = getHeaders();
            const res = await fetch(`${API_BASE}/api/admin/suggestions/${suggestionId}/approve`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ alias, official_term: officialTerm })
            });
            if (res.ok) {
                setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
            } else {
                alert("승인 처리 중 오류가 발생했습니다.");
            }
        } catch (e) {
            console.error("Approve Error:", e);
        }
    };
    const handleReject = async (suggestionId) => {
        if(!window.confirm('이 용어 제안을 삭제(거절)하시겠습니까?')) return;
        try {
            const headers = getHeaders();
            const res = await fetch(`${API_BASE}/api/admin/suggestions/${suggestionId}`, {
                method: 'DELETE',
                headers
            });
            if (res.ok) {
                setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
            } else {
                alert("삭제 처리 중 오류가 발생했습니다.");
            }
        } catch (e) {
            console.error("Reject Error:", e);
        }
    };

    // [핸들러] 로그인
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        const authHeader = 'Basic ' + btoa(username + ':' + password);
        try {
            const res = await fetch(`${API_BASE}/api/admin/check`, { headers: getHeaders(authHeader) });
            if (res.ok) {
                setIsLoggedIn(true);
                sessionStorage.setItem('admin_auth', authHeader);
                fetchData(authHeader);
            } else {
                setError('아이디 또는 비밀번호가 일치하지 않습니다.');
            }
        } catch (err) { setError('서버에 연결할 수 없습니다.'); }
    };

    // [Effect] 초기 로드 시 인증 확인
    useEffect(() => {
        const auth = sessionStorage.getItem('admin_auth');
        if (auth) {
            setIsLoggedIn(true);
            fetchData(auth);
        }
    }, [fetchData]); // 이제 fetchData는 useCallback으로 감싸져 있어 안전함

    // --- Login UI ---
    if (!isLoggedIn) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-100 font-sans">
                <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                    <div className="text-center mb-8">
                        <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100">
                            <Lock className="text-blue-600" size={32} />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800">관리자 접속</h1>
                        <p className="text-sm text-slate-500 mt-2">시스템 모니터링을 위한 인증이 필요합니다.</p>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="relative">
                            <User className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input type="text" placeholder="Admin ID" value={username} onChange={e => setUsername(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm" />
                        </div>
                        <div className="relative">
                            <Key className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm" />
                        </div>
                        {error && <div className="flex items-center gap-2 text-red-500 text-xs font-bold bg-red-50 p-3 rounded-lg"><AlertCircle size={14} /> {error}</div>}
                        <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md">로그인</button>
                    </form>
                </div>
            </div>
        );
    }

    // --- Dashboard UI ---
    const chartData = stats ? {
        labels: stats.top_regulations.map(r => r.name.length > 12 ? r.name.substring(0, 12) + "..." : r.name),
        datasets: [{
            label: '참조 횟수',
            data: stats.top_regulations.map(r => r.count),
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1,
            borderRadius: 4
        }]
    } : null;

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                <div className="flex items-center gap-3">
                    <Link to="/" className="p-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"><Activity size={20} className="text-white" /></Link>
                    <div><h1 className="text-lg font-bold text-slate-800 leading-tight">KIAST AI Admin</h1><p className="text-[10px] text-slate-500 font-medium">System Monitoring Dashboard</p></div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => fetchData()} disabled={loading} className={`p-2 rounded-full transition-all ${loading ? 'bg-slate-100 text-slate-400 animate-spin' : 'hover:bg-slate-100 text-slate-600'}`} title="새로고침"><RefreshCw size={18} /></button>
                    <div className="h-6 w-px bg-slate-200 mx-1"></div>
                    <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"><LogOut size={14} /> 로그아웃</button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto p-6">
                {/* Tabs */}
                <div className="flex gap-6 mb-8 border-b border-slate-200">
                    <button onClick={() => setActiveTab('overview')} className={`pb-3 px-1 flex items-center gap-2 text-sm font-bold transition-all relative ${activeTab === 'overview' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}><BarChart3 size={18} /> 운영 현황 {activeTab === 'overview' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}</button>
                    <button onClick={() => setActiveTab('logs')} className={`pb-3 px-1 flex items-center gap-2 text-sm font-bold transition-all relative ${activeTab === 'logs' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}><List size={18} /> 상세 로그 {activeTab === 'logs' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}</button>
                    <button onClick={() => setActiveTab('glossary')} className={`pb-3 px-1 flex items-center gap-2 text-sm font-bold transition-all relative ${activeTab === 'glossary' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}><CheckCircle size={18} /> 용어 승인 {suggestions.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{suggestions.length}</span>} {activeTab === 'glossary' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}</button>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
                                <div className="flex justify-between items-start"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Users</p><div className="p-2 bg-blue-50 rounded-lg text-blue-600"><Users size={20} /></div></div>
                                <div className="flex items-baseline gap-2"><h3 className="text-3xl font-black text-slate-800">{stats.active_users}</h3><span className="text-xs font-bold text-green-500 bg-green-50 px-1.5 py-0.5 rounded">ONLINE</span></div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
                                <div className="flex justify-between items-start"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Today Sessions</p><div className="p-2 bg-violet-50 rounded-lg text-violet-600"><Activity size={20} /></div></div>
                                <h3 className="text-3xl font-black text-slate-800">{stats.today_visitors}</h3>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
                                <div className="flex justify-between items-start"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Web Latency</p><div className="p-2 bg-cyan-50 rounded-lg text-cyan-600"><Server size={20} /></div></div>
                                <div><div className="flex items-baseline gap-2"><h3 className="text-3xl font-black text-slate-800">{stats.web_latency}</h3><span className="text-sm font-bold text-slate-400">ms</span></div><p className="text-[10px] text-slate-400 mt-1">API / Tree / History</p></div>
                            </div>
                            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
                                <div className="flex justify-between items-start mb-1"><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">AI Processing</p><div className="p-2 bg-rose-50 rounded-lg text-rose-600"><Cpu size={20} /></div></div>
                                <div><div className="flex items-baseline gap-2"><h3 className="text-2xl font-black text-slate-800">{stats.ai_latency.total}</h3><span className="text-xs font-bold text-slate-400">ms (Avg)</span></div><div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 flex overflow-hidden"><div className="bg-orange-400 h-full" style={{ width: `${(stats.ai_latency.rag / stats.ai_latency.total) * 100}%` }}></div><div className="bg-rose-500 h-full" style={{ width: `${(stats.ai_latency.llm / stats.ai_latency.total) * 100}%` }}></div></div><div className="flex justify-between text-[10px] text-slate-500 mt-1 font-mono"><span>RAG: {stats.ai_latency.rag}ms</span><span>LLM: {stats.ai_latency.llm}ms</span></div></div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200"><h3 className="text-sm font-bold text-slate-700 mb-6 flex items-center gap-2"><FileText size={16} className="text-blue-500" /> 많이 참조된 규정 TOP 5</h3><div className="h-64"><Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { display: true, color: '#f1f5f9' } }, x: { grid: { display: false } } } }} /></div></div>
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200"><h3 className="text-sm font-bold text-slate-700 mb-4">규정 참조 상세 현황</h3><div className="space-y-4 overflow-y-auto max-h-64 pr-2 scrollbar-thin">{stats.top_regulations.map((reg, idx) => (<div key={idx} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100"><div className="flex items-center gap-3"><span className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold ${idx < 3 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{idx + 1}</span><span className="text-sm text-slate-600 font-medium truncate w-48 lg:w-64" title={reg.name}>{reg.name}</span></div><span className="text-xs font-mono font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">{reg.count}회</span></div>))}</div></div>
                        </div>
                    </div>
                )}

                {/* Content: Logs */}
                {activeTab === 'logs' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                                    <tr>
                                        <th className="px-2 py-2 whitespace-nowrap w-24 text-center">Time</th>
                                        <th className="px-4 py-2 w-1/5">User Query</th>
                                        <th className="px-4 py-2">AI Response (Click for detail)</th>
                                        <th className="px-4 py-2">References</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-slate-50/80 transition-colors">
                                            {/* 1. Time (Compact) */}
                                            <td className="px-2 py-2 align-top text-center">
                                                {formatCompactTime(log.time)}
                                            </td>

                                            <td className="px-4 py-2 align-top">
                                                <p className="font-medium text-slate-800 line-clamp-3 leading-relaxed" title={log.query}>
                                                    {log.query}
                                                </p>
                                            </td>

                                            {/* 2. AI Response (Modal Trigger) */}
                                            <td className="px-4 py-2 align-top cursor-pointer group" onClick={() => setSelectedLog(log)}>
                                                <div className="relative">
                                                    <p className="text-slate-600 text-xs leading-relaxed line-clamp-3 group-hover:text-blue-700 transition-colors">
                                                        {log.response}
                                                    </p>
                                                    <div className="flex items-center gap-1 mt-0.1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Maximize2 size={10} className="text-blue-400" />
                                                        <span className="text-[10px] text-blue-400 font-bold">전체 보기</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 3. References (Compact) */}
                                            <td className="px-4 py-2 align-top">
                                                <div className="flex flex-col gap-0.5">
                                                    {Array.isArray(log.refs) && log.refs.length > 0 ? (
                                                        log.refs.map((r, i) => (
                                                            <div key={i} className="flex items-center gap-1.5 min-w-0" title={`${r.name} ${r.article}`}>
                                                                <div className="w-1 h-1 rounded-full bg-slate-300 shrink-0"></div>
                                                                <span className="text-[11px] text-slate-600 truncate hover:text-blue-600 cursor-help">
                                                                    {r.rank && <span className="font-bold text-blue-500 mr-1">#{r.rank}</span>}
                                                                    <span className="font-bold">{r.name}</span> <span className="text-slate-400">{r.article}</span>
                                                                    {r.score !== undefined && <span className="text-xs text-slate-400 ml-1">({Number(r.score).toFixed(4)})</span>}
                                                                </span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="text-slate-300 text-xs pl-2">-</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {logs.length === 0 && (
                                        <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-400">데이터가 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Content: Glossary Approval */}
                {activeTab === 'glossary' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden p-6">
                        <h3 className="text-lg font-bold text-slate-800 mb-2">미등록 단어 승인 대기열</h3>
                        <p className="text-sm text-slate-500 mb-6">LLM이 추론한 새로운 용어 매핑 제안입니다. 승인 시 공식 DB에 즉시 반영됩니다.</p>
                        
                        <div className="space-y-4">
                            {suggestions.map(sug => (
                                <div key={sug.id} className="border border-slate-200 rounded-xl p-5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                                            {formatCompactTime(sug.timestamp)}
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-lg text-sm font-bold">
                                                {sug.unmatched_nouns.join(', ')}
                                            </span>
                                            <span className="text-slate-400 text-sm">➔</span>
                                            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm font-bold">
                                                {sug.matched_official_terms.join(', ')}
                                            </span>
                                        </div>
                                        <div className="text-sm text-slate-600 bg-white p-3 rounded border border-slate-100 line-clamp-2">
                                            <span className="font-bold text-slate-400 mr-2">LLM 재구성 결과:</span>
                                            {sug.reconstructed_query}
                                        </div>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-2">
                                        <button 
                                            onClick={() => handleReject(sug.id)}
                                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2.5 rounded-lg font-bold transition-colors shadow-sm"
                                        >
                                            <X size={18} /> 삭제
                                        </button>
                                        <button 
                                            onClick={() => handleApprove(sug.id, sug.unmatched_nouns[0], sug.matched_official_terms[0])}
                                            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-lg font-bold transition-colors shadow-sm"
                                        >
                                            <CheckCircle size={18} /> 승인 및 반영
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {suggestions.length === 0 && (
                                <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                                    승인 대기 중인 용어가 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* AI Response Detail Modal */}
            <AnimatePresence>
                {selectedLog && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setSelectedLog(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><Bot size={20} /></div>
                                    <div>
                                        <h3 className="font-bold text-slate-800">로그 상세 정보</h3>
                                        <p className="text-xs text-slate-500 font-mono">{formatCompactTime(selectedLog.time)} • ID: {selectedLog.id}</p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedLog(null)} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-700 transition-colors"><X size={20} /></button>
                            </div>

                            {/* Modal Body (2-Column Grid) */}
                            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">

                                    {/* Left: Chat Content */}
                                    <div className="flex flex-col gap-6">
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2"><User size={12} /> User Query</h4>
                                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-slate-800 font-medium leading-relaxed text-sm">
                                                {selectedLog.query}
                                            </div>
                                        </div>
                                        <div className="flex-1 min-h-[200px]">
                                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2"><Bot size={12} /> AI Response</h4>
                                            <div className="prose prose-sm prose-slate max-w-none bg-white p-2 text-slate-700 leading-relaxed text-sm border-l-2 border-slate-100 pl-4">
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {selectedLog.response}
                                                </ReactMarkdown>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Analysis Data */}
                                    <div className="flex flex-col gap-6">
                                        {/* Score Chart */}
                                        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center justify-between">
                                                <span>Rerank Score Distribution</span>
                                                <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-slate-200">Threshold: 0.15</span>
                                            </h4>
                                            {/* meta가 문자열이면 파싱, 아니면 그대로 사용 */}
                                            <ScoreChart scores={typeof selectedLog.meta === 'string' ? JSON.parse(selectedLog.meta)?.scores : selectedLog.meta?.scores} />
                                        </div>

                                        {/* Reference List */}
                                        <div className="flex-1 flex flex-col min-h-[200px]">
                                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2"><FileText size={12} /> Referenced Documents</h4>
                                            <div className="max-h-[300px] overflow-y-auto pr-2 scrollbar-thin border border-slate-100 rounded-xl p-2 space-y-2">
                                                {Array.isArray(selectedLog.refs) && selectedLog.refs.length > 0 ? (
                                                    selectedLog.refs.map((r, i) => (
                                                        <div key={i} className="flex items-start justify-between p-3 rounded-lg border border-slate-100 bg-white hover:border-blue-200 transition-colors">
                                                            <div className="flex items-start gap-3">
                                                                <div className="text-[10px] font-mono text-blue-400 mt-0.5 font-bold">#{r.rank || i + 1}</div>
                                                                <div>
                                                                    <div className="text-xs font-bold text-slate-700">{r.name}</div>
                                                                    <div className="text-xs text-slate-500">{r.article}</div>
                                                                </div>
                                                            </div>
                                                            {r.score !== undefined && (
                                                                <div className="text-[10px] text-slate-400 font-mono mt-0.5 whitespace-nowrap">
                                                                    Score: {Number(r.score).toFixed(4)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="h-full flex items-center justify-center text-xs text-slate-400">참조 문서 없음</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}